/**
 * Shared filter ↔ URL roundtrip for the public-site pages
 * (Inventory + every analytics tab). Without this, each page held
 * the filter in local React state and tab navigation reset it; with
 * it, the URL `?…` query string is the canonical filter shape and
 * NavBar carries it across tab switches.
 *
 * Predicates supported (must stay in lockstep with `CarFilter` in
 * `api/client.ts` and spec §2.4.2):
 *  - models, family_codes, variant_codes, trim_codes
 *  - model_years
 *  - drivetrains
 *  - body_types
 *  - battery_kwh_values
 *  - interior_materials
 *  - body_classes, size_classes
 *  - capabilities_any_of, capabilities_excluded
 *  - price_min, price_max, mileage_min, mileage_max
 *  - include_disappeared, only_disappeared, disappeared_within_days
 *
 * Brand is NOT in the URL — it's a navigation concern owned by
 * BrandContext / route, not the filter primitive.
 */

import { type CarFilter } from "../api/client";

export function filterFromParams(params: URLSearchParams): CarFilter {
  const f: CarFilter = {};
  const models = params.getAll("models");
  if (models.length) f.models = models;
  const priceMin = params.get("price_min");
  if (priceMin !== null) f.price_min = Number(priceMin);
  const priceMax = params.get("price_max");
  if (priceMax !== null) f.price_max = Number(priceMax);
  const mileageMin = params.get("mileage_min");
  if (mileageMin !== null) f.mileage_min = Number(mileageMin);
  const mileageMax = params.get("mileage_max");
  if (mileageMax !== null) f.mileage_max = Number(mileageMax);
  const inc = params.get("include_disappeared");
  if (inc !== null) f.include_disappeared = inc === "true";
  const onlyGone = params.get("only_disappeared");
  if (onlyGone !== null) f.only_disappeared = onlyGone === "true";
  const withinDays = params.get("disappeared_within_days");
  if (withinDays !== null) f.disappeared_within_days = Number(withinDays);
  const capInc = params.getAll("capabilities_any_of");
  if (capInc.length) f.capabilities_any_of = capInc;
  const capExc = params.getAll("capabilities_excluded");
  if (capExc.length) f.capabilities_excluded = capExc;
  const bodyClasses = params.getAll("body_classes");
  if (bodyClasses.length) f.body_classes = bodyClasses;
  const sizeClasses = params.getAll("size_classes");
  if (sizeClasses.length) f.size_classes = sizeClasses;
  const familyCodes = params.getAll("family_codes");
  if (familyCodes.length) f.family_codes = familyCodes;
  const variantCodes = params.getAll("variant_codes");
  if (variantCodes.length) f.variant_codes = variantCodes;
  const trimCodes = params.getAll("trim_codes");
  if (trimCodes.length) f.trim_codes = trimCodes;
  const modelYears = params.getAll("model_years");
  if (modelYears.length) f.model_years = modelYears.map(Number);
  const drivetrains = params.getAll("drivetrains");
  if (drivetrains.length) f.drivetrains = drivetrains;
  const bodyTypes = params.getAll("body_types");
  if (bodyTypes.length) f.body_types = bodyTypes;
  const batteryKwhValues = params.getAll("battery_kwh_values");
  if (batteryKwhValues.length) f.battery_kwh_values = batteryKwhValues.map(Number);
  const interiorMaterials = params.getAll("interior_materials");
  if (interiorMaterials.length) f.interior_materials = interiorMaterials;
  return f;
}

export function filterToParams(f: CarFilter): URLSearchParams {
  const p = new URLSearchParams();
  f.models?.forEach((m) => p.append("models", m));
  if (f.price_min !== undefined) p.set("price_min", String(f.price_min));
  if (f.price_max !== undefined) p.set("price_max", String(f.price_max));
  if (f.mileage_min !== undefined) p.set("mileage_min", String(f.mileage_min));
  if (f.mileage_max !== undefined) p.set("mileage_max", String(f.mileage_max));
  if (f.include_disappeared !== undefined)
    p.set("include_disappeared", String(f.include_disappeared));
  if (f.only_disappeared !== undefined)
    p.set("only_disappeared", String(f.only_disappeared));
  if (f.disappeared_within_days !== undefined)
    p.set("disappeared_within_days", String(f.disappeared_within_days));
  f.capabilities_any_of?.forEach((s) => p.append("capabilities_any_of", s));
  f.capabilities_excluded?.forEach((s) => p.append("capabilities_excluded", s));
  f.body_classes?.forEach((c) => p.append("body_classes", c));
  f.size_classes?.forEach((c) => p.append("size_classes", c));
  f.family_codes?.forEach((c) => p.append("family_codes", c));
  f.variant_codes?.forEach((c) => p.append("variant_codes", c));
  f.trim_codes?.forEach((c) => p.append("trim_codes", c));
  f.model_years?.forEach((y) => p.append("model_years", String(y)));
  f.drivetrains?.forEach((d) => p.append("drivetrains", d));
  f.body_types?.forEach((b) => p.append("body_types", b));
  f.battery_kwh_values?.forEach((d) => p.append("battery_kwh_values", String(d)));
  f.interior_materials?.forEach((m) => p.append("interior_materials", m));
  return p;
}
